import {
  AppLifecycle,
  AppLifecycleCapabilities,
  AppLifecyclePermissionResult,
  AppLifecycleSchedule,
  isAppLifecycleAvailable,
} from "../plugins/appLifecycle";
import { getDecryptedUserData } from "./bycrypt";
import { getBusinessTimeZone } from "./moment";
import { userStorage } from "./userStorage";
import { ROLE } from "./utils";
import {
  AttendanceAppSchedule,
  BUSINESS_FALLBACK_TIME_ZONE,
  computeAppLifecycleTriggers,
  isValidTimeZone,
} from "./appLifecycleTime";

// Pure time logic (unit tested) — re-exported so callers only need this entry point.
export {
  computeAppLifecycleTriggers,
  nextOccurrenceMs,
} from "./appLifecycleTime";
export type {
  AppLifecycleTriggers,
  AttendanceAppSchedule,
} from "./appLifecycleTime";

/**
 * appLifecycleScheduler — JavaScript half of the attendance app's
 * "auto start / auto close" feature.
 *
 * Division of responsibility (mirrors the native side):
 *   - THIS file owns all time logic: it turns the business setting
 *     `attendance_app_schedule` ({ isAutoStartStopEnabled, autoStartTime "HH:mm",
 *     autoCloseTime "HH:mm" } — times expressed in the timezone configured on
 *     the Settings screen and stored as `business_settings.timezone`) into two
 *     absolute epoch-millisecond triggers.
 *   - `AppLifecycle` (native) only executes those instants — surviving Doze, a locked
 *     screen, a dead process and even a reboot — and kills / relaunches the app.
 *
 * The schedule is (re)armed:
 *   1. once when the app boots ({@link initAppLifecycleScheduler}),
 *   2. every time business settings change (`business-settings-updated` window event),
 *   3. after every schedule driven launch (`autoLaunched` event — the consumed
 *      triggers have to be re-armed for the next day),
 *   4. every REARM_INTERVAL_MS as a safety net while the app runs.
 *
 * Two rules make the daily close / open cycle dependable:
 *   - it only arms on an attendance-tablet device (an unknown role keeps the armed
 *     schedule instead of wiping it — see `resolveAttendanceTabletDevice`),
 *   - a trigger native has stored but not delivered yet is never replaced by tomorrow's
 *     occurrence (see `pendingTriggerAt`), so a re-arm can not skip a whole cycle.
 */

// ---------------------------------------------------------------- settings ----

/** Window event fired whenever persisted business settings change. */
export const BUSINESS_SETTINGS_UPDATED_EVENT = "business-settings-updated";
/** Fired natively right before the process is killed (during the grace period). */
export const APP_ABOUT_TO_CLOSE_EVENT = "app-lifecycle-about-to-close";
/** Fired natively when the schedule (re)opened the app. */
export const APP_AUTO_LAUNCHED_EVENT = "app-lifecycle-auto-launched";

/** How long JavaScript gets to flush state before the native side kills the process. */
const CLOSE_GRACE_MS = 10_000;
/** A reopen missed while the device was off is still recovered within this window. */
const MISSED_REOPEN_GRACE_MS = 12 * 60 * 60 * 1000;
/** Safety re-arm interval while the app is running (picks up changed settings). */
const REARM_INTERVAL_MS = 30 * 60 * 1000;
/**
 * How late a trigger that native has stored but not delivered yet may still be honoured
 * (see `pendingTriggerAt`). Beyond this window the trigger counts as lost / stale and the
 * freshly computed next occurrence wins, so the schedule can never get stuck on an old
 * moment. Needed on devices where exact alarms are unavailable: AlarmManager may then
 * hold a trigger back by up to about 15 minutes.
 */
const PENDING_TRIGGER_MAX_WAIT_MS = 30 * 60 * 1000;
/**
 * Only arm the schedule on an attendance-tablet device, so a manager using the same
 * app on their phone is never killed / relaunched. Set to false to arm the schedule
 * for every device synced into this business.
 */
const ARM_ONLY_ON_ATTENDANCE_TABLET = true;

// ------------------------------------------------------------- time logic -----

/**
 * The time zone the schedule's HH:mm values are expressed in.
 *
 * Source order (all of them are ultimately the value set on the Settings screen):
 *   1. `business_settings.timezone` (handed in by the caller),
 *   2. the logged in business profile's timezone (`getBusinessTimeZone`),
 *   3. the business default (`BUSINESS_FALLBACK_TIME_ZONE`).
 * `moment.tz.guess()` is deliberately NOT used, so a device set to another zone can
 * never shift the configured close/open moments.
 */
function safeTimeZone(): string {
  try {
    const tz = getBusinessTimeZone();
    if (isValidTimeZone(tz)) return tz;
  } catch {
    /* fall through */
  }
  return BUSINESS_FALLBACK_TIME_ZONE;
}

/**
 * The slice of `business_settings` that drives the auto start / auto close
 * feature — the same values the Settings screen shows: the
 * `attendance_app_schedule` object plus the business `timezone` picked there.
 */
export interface BusinessSettingsSnapshot {
  timezone?: unknown;
  attendance_app_schedule?: AttendanceAppSchedule | null;
}

/**
 * Normalises a time zone as stored in business settings — either a plain IANA
 * name ("Asia/Kolkata") or a decorated one ("Asia/Kolkata (IST)") — to a name
 * moment-timezone accepts. Returns `null` for missing / unusable values.
 */
function normalizeZone(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const candidate = raw.trim().split(" ")[0];
  return isValidTimeZone(candidate) ? candidate : null;
}

// ------------------------------------------------------------- permissions ----

export type AppLifecyclePermissionStep =
  | "notifications"
  | "exactAlarm"
  | "fullScreenIntent"
  | "overlay"
  | "batteryOptimization"
  | "autoStart";

export interface AppLifecycleMissingStep {
  step: AppLifecyclePermissionStep;
  label: string;
}

export interface AppLifecycleReadiness {
  available: boolean;
  capabilities: AppLifecycleCapabilities | null;
  /** Privileges that are still missing for a dependable auto start / auto close. */
  missing: AppLifecycleMissingStep[];
  /** The platform can be trusted to reopen the app by itself. */
  ready: boolean;
}

const STEP_LABELS: Record<AppLifecyclePermissionStep, string> = {
  notifications: "Notifications",
  exactAlarm: "Exact alarms",
  fullScreenIntent: "Full screen intent",
  overlay: "Display over other apps",
  batteryOptimization: "Unrestricted battery usage",
  autoStart: "Auto start (manufacturer setting)",
};

/** Current native capability state + the list of privileges still missing. */
export async function getAppLifecycleReadiness(): Promise<AppLifecycleReadiness> {
  if (!isAppLifecycleAvailable()) {
    return { available: false, capabilities: null, missing: [], ready: false };
  }

  try {
    const capabilities = await AppLifecycle.capabilities();
    const missing: AppLifecycleMissingStep[] = [];

    if (!capabilities.notificationsAllowed) {
      missing.push({ step: "notifications", label: STEP_LABELS.notifications });
    }
    if (!capabilities.exactAlarmsAllowed) {
      missing.push({ step: "exactAlarm", label: STEP_LABELS.exactAlarm });
    }
    if (!capabilities.fullScreenIntentAllowed && !capabilities.overlayAllowed) {
      // Either privilege is enough to open the activity over the lock screen.
      missing.push({
        step: "fullScreenIntent",
        label: STEP_LABELS.fullScreenIntent,
      });
    }
    if (!capabilities.ignoringBatteryOptimizations) {
      missing.push({
        step: "batteryOptimization",
        label: STEP_LABELS.batteryOptimization,
      });
    }

    return {
      available: true,
      capabilities,
      missing,
      ready: capabilities.backgroundLaunchGuaranteed,
    };
  } catch (error) {
    console.error("[AppLifecycle] capabilities() failed:", error);
    return { available: false, capabilities: null, missing: [], ready: false };
  }
}

/** What the individual native permission screens resolve to. */
export type AppLifecyclePermissionOutcome =
  | AppLifecyclePermissionResult
  | { granted: boolean; state: string }
  | { opened: boolean; target?: string };

/**
 * Opens the system screen for one missing privilege (or the OEM auto start screen).
 * Re-check {@link getAppLifecycleReadiness} afterwards — the user still has to
 * confirm in the settings screen that just opened.
 */
export function requestAppLifecyclePermission(
  step: AppLifecyclePermissionStep,
): Promise<AppLifecyclePermissionOutcome> {
  switch (step) {
    case "notifications":
      return AppLifecycle.requestNotificationPermission();
    case "exactAlarm":
      return AppLifecycle.requestExactAlarmPermission();
    case "fullScreenIntent":
      return AppLifecycle.requestFullScreenIntentPermission();
    case "overlay":
      return AppLifecycle.requestOverlayPermission();
    case "batteryOptimization":
      return AppLifecycle.requestIgnoreBatteryOptimizations();
    case "autoStart":
      return AppLifecycle.requestAutoStartPermission();
    default:
      return Promise.reject(new Error(`Unknown permission step: ${step}`));
  }
}

// ---------------------------------------------------------------- schedule -----

/**
 * Mirrors `AttendanceTabletGuard` (App.tsx) — only that device role runs the kiosk
 * screen, so only that device may be closed / relaunched by the schedule.
 *
 * Tri-state on purpose: `null` = "cannot tell right now" (no decrypted user yet —
 * cold start, logged out kiosk, decryption error). An unknown role must NOT be treated
 * as "not a tablet": a transient identity problem would otherwise wipe a schedule that
 * native already has armed and the kiosk would stop closing at the configured time.
 * Only an explicit `false` is a non-kiosk device.
 */
function resolveAttendanceTabletDevice(): boolean | null {
  try {
    const userData: any = getDecryptedUserData();
    if (!userData) return null;
    const roleKey = userData?.role?.key || "";
    const roleName = String(userData?.role?.name || "").toLowerCase();
    return (
      roleKey === ROLE.ATTENDANCE_TABLET ||
      roleKey === "attendance-tablet" ||
      roleName.includes("attendance tablet") ||
      roleName.includes("attendance-tablet")
    );
  } catch {
    return null;
  }
}

/**
 * Resolves the moment that STILL has to be honoured for a trigger the native side has
 * stored but not delivered yet (`null` = use the freshly computed next occurrence).
 *
 * Native clears a trigger the instant it fires, so "stored but not armed" means the
 * alarm is owed and undelivered (Doze / a delayed inexact alarm when exact alarms are
 * unavailable). Replacing it with tomorrow's occurrence would silently skip a whole
 * close/open cycle — i.e. the app would stay open all night.
 *
 * `PENDING_TRIGGER_MAX_WAIT_MS` bounds how late such a trigger may still be honoured:
 * a genuinely lost trigger is older than that, so the app can never get stuck on an
 * old moment.
 */
function pendingTriggerAt(
  storedAt: number | null | undefined,
  armed: boolean | undefined,
): number | null {
  if (!storedAt || storedAt <= 0 || armed) return null;
  const late = Date.now() - storedAt;
  if (late < 0 || late > PENDING_TRIGGER_MAX_WAIT_MS) return null;
  return storedAt;
}

/**
 * Reads the persisted business settings that drive the schedule — the
 * `attendance_app_schedule` object and the business `timezone`, both as shown
 * on the Settings screen (empty object when never synced).
 *
 * @returns the stored settings, or `null` when storage could not be read — the caller
 *          must then keep the existing native schedule untouched instead of clearing it.
 */
export async function loadPersistedBusinessSettings(): Promise<BusinessSettingsSnapshot | null> {
  try {
    const settings = await userStorage.getBusinessSettings();
    return settings ?? {};
  } catch (error) {
    console.error(
      "[AppLifecycle] Unable to read persisted business settings:",
      error,
    );
    return null;
  }
}

/**
 * Computes the next close / reopen instants and (re)arms the native triggers.
 *
 * The HH:mm values of `attendance_app_schedule` are interpreted in the business
 * time zone stored next to them in `business_settings.timezone` (the timezone
 * shown on the Settings screen). When that value is missing or unusable the
 * zone of the logged-in business profile is used, finally the device zone —
 * never a hard-coded one. The native side only ever receives absolute epoch
 * milliseconds, so no time zone is applied again there.
 *
 * @param settingsOverride freshly saved settings (from the `business-settings-updated`
 *        event) merged over the persisted copy — when omitted / null only the
 *        persisted business settings are used.
 * @returns the armed schedule, or null when the plugin is unavailable / call failed.
 */
export async function syncAppLifecycleSchedule(
  settingsOverride?: BusinessSettingsSnapshot | null,
): Promise<AppLifecycleSchedule | null> {
  if (!isAppLifecycleAvailable()) return null;

  try {
    const persisted = await loadPersistedBusinessSettings();

    if (persisted === null && !settingsOverride) {
      // Storage was not ready (e.g. cold start before the DB opened) — never treat
      // this as "disabled" or we would wipe alarms native already has armed.
      console.warn(
        "[AppLifecycle] Business settings unreadable — keeping the armed schedule",
      );
      return null;
    }

    const settings: BusinessSettingsSnapshot = {
      ...(persisted ?? {}),
      ...(settingsOverride ?? {}),
    };
    const schedule = settings.attendance_app_schedule ?? {};
    const timeZone = normalizeZone(settings.timezone) ?? safeTimeZone();
    const triggers = computeAppLifecycleTriggers(schedule, timeZone);

    // Respect the setting first: it is a business fact that does not depend on this
    // device, so turning the feature off (or a missing / malformed time) always clears
    // the schedule immediately.
    if (!triggers.enabled) {
      const cleared = await AppLifecycle.clearSchedule();
      console.log("[AppLifecycle] Auto start/stop disabled — schedule cleared");
      return cleared;
    }

    if (ARM_ONLY_ON_ATTENDANCE_TABLET) {
      const isAttendanceTablet = resolveAttendanceTabletDevice();

      if (isAttendanceTablet === false) {
        // Definitely a non-kiosk device (e.g. a manager's phone): never kill / relaunch it.
        await AppLifecycle.clearSchedule();
        console.log(
          "[AppLifecycle] Not an attendance-tablet device — schedule cleared",
        );
        return null;
      }

      if (isAttendanceTablet === null) {
        // The device role cannot be read yet (cold start before login, logged out kiosk,
        // decryption error). Keep whatever native already has armed instead of wiping it.
        console.warn(
          "[AppLifecycle] Device role unknown — keeping the armed schedule",
        );
        return null;
      }
    }

    // A re-arm can land just after a trigger's moment (screen wake, the periodic tick, a
    // delayed alarm). `computeAppLifecycleTriggers` always resolves the NEXT occurrence,
    // so applying it blindly would push a still owed trigger a whole day ahead. Read the
    // armed state first and keep an owed moment (see `pendingTriggerAt`).
    let armed: AppLifecycleSchedule | null = null;
    try {
      armed = await AppLifecycle.getSchedule();
    } catch (readError) {
      // Reading is best effort — fall back to the freshly computed triggers.
      console.warn("[AppLifecycle] getSchedule() failed:", readError);
    }

    const pendingCloseAt = pendingTriggerAt(armed?.closeAt, armed?.closeArmed);
    const pendingReopenAt = pendingTriggerAt(
      armed?.reopenAt,
      armed?.reopenArmed,
    );

    const closeAt = pendingCloseAt ?? triggers.closeAt;
    const reopenAt = pendingReopenAt ?? triggers.reopenAt;

    if (pendingCloseAt || pendingReopenAt) {
      console.warn(
        `[AppLifecycle] Honouring trigger(s) still owed — close ${
          pendingCloseAt ?? "-"
        } / reopen ${pendingReopenAt ?? "-"}`,
      );
    }

    const result = await AppLifecycle.setSchedule({
      closeAt,
      reopenAt,
      reopenMode: "exact",
      wakeScreen: true,
      closeGraceMs: CLOSE_GRACE_MS,
      recoverMissedReopen: true,
      missedGraceMs: MISSED_REOPEN_GRACE_MS,
    });

    console.log(
      `[AppLifecycle] Armed — close ${new Date(closeAt).toISOString()} / ` +
        `reopen ${new Date(reopenAt).toISOString()}` +
        ` (settings ${schedule.autoStartTime ?? "?"} / ${
          schedule.autoCloseTime ?? "?"
        } in ${timeZone})`,
    );
    return result;
  } catch (error) {
    console.error("[AppLifecycle] schedule sync failed:", error);
    return null;
  }
}

// ------------------------------------------------------------------ init ------

let initCleanup: (() => void) | null = null;

/**
 * Starts the JS side of the scheduler (idempotent). Returns the teardown function.
 * Mounted once from `App` — see `AppLifecycleSchedulerHandler`.
 */
export function initAppLifecycleScheduler(): () => void {
  if (!isAppLifecycleAvailable()) {
    console.log("[AppLifecycle] Not a native build — scheduler idle");
    return () => undefined;
  }
  if (initCleanup) return initCleanup;

  const handleAboutToClose = (data: {
    trigger: string;
    at: number;
    graceMs: number;
  }) => {
    console.log(
      `[AppLifecycle] Closing in ${data.graceMs}ms — persist state now`,
      data,
    );
    document.dispatchEvent(
      new CustomEvent(APP_ABOUT_TO_CLOSE_EVENT, { detail: data }),
    );
  };

  const handleAutoLaunched = (data: {
    trigger: string;
    at: number;
    recovered: boolean;
  }) => {
    console.log("[AppLifecycle] App (re)opened by the schedule", data);
    // The triggers were consumed by native — arm the next day's pair immediately.
    void syncAppLifecycleSchedule();
    document.dispatchEvent(
      new CustomEvent(APP_AUTO_LAUNCHED_EVENT, { detail: data }),
    );
  };

  const handleSettingsUpdated = (event: Event) => {
    const detail = (event as CustomEvent).detail;
    // Pass the full just-saved settings (schedule AND timezone) so they can be
    // merged over the persisted copy and re-armed immediately.
    const snapshot =
      detail && typeof detail === "object"
        ? (detail as BusinessSettingsSnapshot)
        : null;
    void syncAppLifecycleSchedule(snapshot);
  };

  const handleVisibility = () => {
    if (document.visibilityState === "visible") void syncAppLifecycleSchedule();
  };

  const timers: ReturnType<typeof setInterval>[] = [];
  const disposers: (() => void)[] = [];

  AppLifecycle.addListener("aboutToClose", handleAboutToClose)
    .then((handle) => disposers.push(() => handle.remove()))
    .catch((error) =>
      console.error("[AppLifecycle] aboutToClose listener failed:", error),
    );

  AppLifecycle.addListener("autoLaunched", handleAutoLaunched)
    .then((handle) => disposers.push(() => handle.remove()))
    .catch((error) =>
      console.error("[AppLifecycle] autoLaunched listener failed:", error),
    );

  window.addEventListener(
    BUSINESS_SETTINGS_UPDATED_EVENT,
    handleSettingsUpdated,
  );
  disposers.push(() =>
    window.removeEventListener(
      BUSINESS_SETTINGS_UPDATED_EVENT,
      handleSettingsUpdated,
    ),
  );

  document.addEventListener("visibilitychange", handleVisibility);
  disposers.push(() =>
    document.removeEventListener("visibilitychange", handleVisibility),
  );

  timers.push(
    setInterval(() => void syncAppLifecycleSchedule(), REARM_INTERVAL_MS),
  );

  // First arm of this session + a readiness report for the debug console.
  void syncAppLifecycleSchedule().then(async () => {
    const readiness = await getAppLifecycleReadiness();
    if (readiness.available) {
      console.log(
        `[AppLifecycle] ready=${readiness.ready} missing=[${readiness.missing
          .map((entry) => entry.step)
          .join(", ")}]`,
      );
    }
  });

  initCleanup = () => {
    timers.forEach((timer) => clearInterval(timer));
    disposers.forEach((dispose) => {
      try {
        dispose();
      } catch {
        /* listener already gone */
      }
    });
    initCleanup = null;
  };

  return initCleanup;
}
